type ModulePattern = { pattern: string; hostKey: string };

const REACT_PATTERNS: ModulePattern[] = [
  { pattern: 'jsx-dev-runtime', hostKey: 'react/jsx-dev-runtime' },
  { pattern: 'jsx-runtime', hostKey: 'react/jsx-runtime' },
  { pattern: 'react-dom/client', hostKey: 'react-dom/client' },
  { pattern: 'react-dom', hostKey: 'react-dom/index' },
];

const NEXT_PATTERNS: ModulePattern[] = [
  { pattern: '/compat/router', hostKey: 'next/compat/router' },
  { pattern: '/navigation', hostKey: 'next/navigation' },
  { pattern: '/router', hostKey: 'next/router' },
  { pattern: '/link', hostKey: 'next/link' },
  { pattern: '/image', hostKey: 'next/image' },
];

const findMatch = (
  moduleId: string,
  patterns: ModulePattern[],
  host: Record<string, unknown>,
  fallbackKey?: string,
): unknown => {
  for (const { pattern, hostKey } of patterns) {
    if (moduleId.includes(pattern) && host[hostKey]) return host[hostKey];
  }
  return fallbackKey ? host[fallbackKey] : undefined;
};

export const matchHostModule = (
  moduleId: string,
  host: Record<string, unknown>,
): unknown => {
  if (
    moduleId.includes('/node_modules/react/') ||
    moduleId.includes('/node_modules/react-dom/')
  ) {
    return findMatch(moduleId, REACT_PATTERNS, host, 'react/index');
  }

  if (moduleId.includes('/node_modules/next/')) {
    return findMatch(moduleId, NEXT_PATTERNS, host);
  }

  return undefined;
};

type SignatureCheck = {
  required: string[];
  excluded?: string[];
  hostKey: string;
};

const SIGNATURE_CHECKS: SignatureCheck[] = [
  { required: ['createElement', 'useState'], hostKey: 'react/index' },
  { required: ['createRoot', 'hydrateRoot'], hostKey: 'react-dom/client' },
  {
    required: ['flushSync', 'createPortal'],
    excluded: ['createRoot'],
    hostKey: 'react-dom/index',
  },
  { required: ['jsx', 'jsxs'], hostKey: 'react/jsx-runtime' },
  { required: ['jsxDEV'], hostKey: 'react/jsx-dev-runtime' },
  {
    required: ['useRouter', 'usePathname', 'useSearchParams'],
    excluded: ['useIsFederated', 'NavigationProvider'],
    hostKey: 'next/navigation',
  },
  { required: ['useRouter', 'withRouter'], hostKey: 'next/router' },
];

export const detectAndSwapReactModule = (
  mod: Record<string, unknown>,
  host: Record<string, unknown>,
) => {
  const exports = mod.exports as Record<string, unknown>;
  if (!exports || typeof exports !== 'object' || Array.isArray(exports)) return;

  for (const check of SIGNATURE_CHECKS) {
    const requiredMatch = check.required.every(
      key => typeof exports[key] === 'function',
    );
    const excludedMatch = check.excluded?.some(
      key => typeof exports[key] === 'function',
    );

    if (requiredMatch && !excludedMatch && host[check.hostKey]) {
      mod.exports = host[check.hostKey];
      return;
    }
  }
};
